import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVideoWorkspaceStore, SlideConversation } from '@/stores/videoWorkspaceStore';
import {
  List,
  Layers,
  Loader2,
  Check,
  AlertCircle,
  MessageCircle,
  Radio,
  FileText,
} from 'lucide-react';

interface IvyContextSwitcherProps {
  parentConversationId: string;
  onContextPrefixChange: (prefix: string) => void;
  slides?: Array<{ id: string; title: string; childConversationId?: string; status?: string }>;
}

export const IvyContextSwitcher: React.FC<IvyContextSwitcherProps> = ({
  parentConversationId: _parentConversationId,
  onContextPrefixChange,
  slides = [],
}) => {
  const {
    activeChatContext,
    setActiveChatContext,
    slideConversations,
  } = useVideoWorkspaceStore();

  const effectiveSlideOptions = slideConversations.length > 0
    ? slideConversations.filter((conv) => conv.id && conv.id.length > 0)
    : slides
        .filter((slide) => slide.childConversationId)
        .map((slide) => ({
          id: slide.childConversationId!,
          outlineSlideId: slide.id,
          title: slide.title,
          status: (slide.status || 'completed') as SlideConversation['status'],
        }));

  const hasSlideOptions = effectiveSlideOptions.length > 0;

  const handleValueChange = (value: string) => {
    setActiveChatContext(value);

    if (value === 'outline') {
      onContextPrefixChange('[Context: Video Outline — working on the project outline and structure]');
    } else if (value === 'narration') {
      onContextPrefixChange('[Context: Narration — writing/editing narration scripts for slides]');
    } else if (value === 'broadcast') {
      onContextPrefixChange('[Context: Broadcast — apply the following instruction to ALL slides in this project]');
    } else {
      const slideIndex = effectiveSlideOptions.findIndex(s => s.id === value);
      const slide = effectiveSlideOptions[slideIndex];
      if (slide) {
        onContextPrefixChange(`[Context: Slide ${slideIndex + 1} "${slide.title}" — working specifically on this slide]`);
      }
    }
  };

  const StatusIcon: React.FC<{ status: SlideConversation['status'] }> = ({ status }) => {
    switch (status) {
      case 'generating':
        return <Loader2 className="w-3 h-3 animate-spin text-primary" />;
      case 'completed':
        return <Check className="w-3 h-3 text-emerald-400" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-400" />;
      default:
        return <div className="w-3 h-3 rounded-full bg-zinc-600" />;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded-lg bg-accent flex items-center justify-center">
        <MessageCircle className="w-3.5 h-3.5 text-accent-foreground" />
      </div>
      <Select value={activeChatContext} onValueChange={handleValueChange}>
        <SelectTrigger className="h-6 w-auto min-w-[80px] max-w-[160px] md:max-w-[200px] border-0 bg-transparent text-xs font-semibold studio-text-primary focus:ring-0 focus:ring-offset-0 px-1">
          <SelectValue placeholder="Select context" />
        </SelectTrigger>
        <SelectContent className="min-w-[180px] max-w-[calc(100vw-2rem)]">
          <SelectItem value="outline" className="cursor-pointer">
            <div className="flex items-center gap-2">
              <List className="w-4 h-4 text-primary" />
              <span>Whole video</span>
            </div>
          </SelectItem>

          <SelectItem value="narration" className="cursor-pointer">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <span>Narration Scripts</span>
            </div>
          </SelectItem>

          {hasSlideOptions && (
            <>
              <div className="h-px bg-border my-1" />
              <SelectItem value="broadcast" className="cursor-pointer">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-amber-400" />
                  <span>Broadcast to All</span>
                  <span className="text-xs text-muted-foreground">
                    ({effectiveSlideOptions.length})
                  </span>
                </div>
              </SelectItem>
            </>
          )}

          {hasSlideOptions && (
            <div className="h-px bg-border my-1" />
          )}

          {effectiveSlideOptions.map((conv, index) => (
            <SelectItem key={conv.id} value={conv.id} className="cursor-pointer">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-zinc-400" />
                <span className="truncate max-w-[140px] md:max-w-[180px]">
                  Slide {index + 1}: {conv.title}
                </span>
                <StatusIcon status={conv.status} />
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
